<template>
	<QuestionGroup>
		<!-- Enable Advanced Features Toggle -->
		<QuestionCard
			key="advanced-enable"
			title="Would you like to enable advanced features?"
			description="Advanced features let you interact with a consent banner, simulate sending a GPC signal, and more."
			:show-yes-no-buttons="siteScannerFullAccess"
			:value="advancedEnabled === true"
			@input="onToggleAdvanced($event)"
		/>

		<transition name="fade-expand">
			<div
				v-if="advancedEnabled"
				class="advanced-dynamic-group"
			>
				<!-- Banner Interaction opt-in -->
				<QuestionCard
					key="banner-interaction"
					title="Would you like to interact with a consent or cookie banner before scanning?"
					description=" If yes, supply a unique button selector (CSS class or ID) that should be clicked before scanning pages."
					:show-yes-no-buttons="siteScannerFullAccess"
					:value="provideBannerInteraction"
					@input="onToggleBannerInteraction"
				>
					<template v-if="provideBannerInteraction">
						<v-row dense>
							<v-col cols="6">
								<TextField
									:value="siteScan.buttonSelector"
									label="Button Class or ID"
									:disabled="!siteScannerFullAccess"
									:explainer="buttonSelectorTooltip"
									@input="updateScanRequest('buttonSelector', $event)"
								/>
							</v-col>
						</v-row>
					</template>
				</QuestionCard>

				<!-- Domains opt-in -->
				<QuestionCard
					key="domains"
					title="Would you like to allow additional domains during the crawl?"
					description="By default, the scanner only crawls the starting domain. Add any other trusted domains here if you’d like the scan to continue across them when crawling."
					:show-yes-no-buttons="siteScannerFullAccess"
					:value="provideDomains"
					@input="onToggleDomains"
				>
					<template v-if="provideDomains">
						<v-row dense>
							<v-col cols="6">
								<OptionSelector
									:selected="siteScan.allowedDomains"
									entity-type="Domain"
									is-text
									display-as-chips
									:disabled="!siteScannerFullAccess"
									@update:selected="onDomainsUpdated"
								/>
							</v-col>
						</v-row>
					</template>
				</QuestionCard>

				<!-- Performance & Robots opt-in -->
				<QuestionCard
					key="performance"
					title="Would you like to adjust the page load timeout?"
					description="Set how long the scanner should wait before moving to the next page."
					:show-yes-no-buttons="siteScannerFullAccess"
					:value="providePerformanceSettings"
					@input="onTogglePerformance"
				>
					<template v-if="providePerformanceSettings">
						<v-row dense>
							<v-col cols="6">
								<ValueSlider
									class="ml-2"
									:value="normalizedPageTimeout"
									:min="1"
									:max="10"
									max-type="Seconds"
									:step="1"
									:ticks="true"
									tick-size="2"
									thumb-label="always"
									:disabled="!siteScannerFullAccess"
									aria-label="Page timeout in seconds"
									:show-current="true"
									@input="onPageTimeoutChanged"
								>
									<template #current="{ value }">
										Current: {{ value }} second{{ value === 1 ? '' : 's' }} (default 5)
									</template>
								</ValueSlider>
							</v-col>
						</v-row>
					</template>
				</QuestionCard>

				<!-- Robots.txt separate card -->
				<QuestionCard
					v-if="showRobotsTxtToggle && advancedEnabled"
					key="robots"
					title="Follow robots.txt directives?"
					description="Respect rules from the site's robots.txt during the scan. Disable to ignore disallow directives."
					:show-yes-no-buttons="siteScannerFullAccess"
					:value="siteScan.followRobotsTxt"
					@input="onRobotsToggle"
				/>

				<!-- Headless direct yes/no (always allowed) -->
				<QuestionCard
					key="headless"
					title="Would you like to run the scan in headless mode?"
					description="Headless mode may speed up scans by skipping full visual rendering of pages."
					:show-yes-no-buttons="siteScannerFullAccess"
					:value="siteScan.headless"
					@input="onHeadlessChanged"
				/>

				<!-- Login Template opt-in -->
				<QuestionCard
					v-if="loginTemplateItems.length > 0"
					key="login-template"
					title="Do you need to authenticate with a login template before scanning?"
					description="If your site requires login, choose a pre-configured template to authenticate before scanning secured pages."
					:show-yes-no-buttons="siteScannerFullAccess"
					:value="provideLoginTemplate"
					@input="onToggleLoginTemplate"
				>
					<template v-if="provideLoginTemplate">
						<v-row>
							<v-col cols="6">
								<Dropdown
									:value="siteScan.loginTemplate"
									label="Login Template"
									:items="loginTemplateItems"
									clearable
									:disabled="!siteScannerFullAccess"
									@change="onLoginTemplateChanged"
								/>
							</v-col>
						</v-row>
						<div
							v-if="siteScan.loginTemplate"
							class="text-caption mt-2"
						>
							<span class="font-weight-bold">
								Description:
							</span>
							{{ loginTemplateDescription }}
						</div>
					</template>
				</QuestionCard>

				<!-- GPC opt-in -->
				<QuestionCard
					key="gpc"
					title="Would you like to send a Global Privacy Control (GPC) signal?"
					description="Adds the SEC-GPC header to indicate that the user opts out of personal data sale/sharing"
					:show-yes-no-buttons="siteScannerFullAccess"
					:value="siteScan.gpcEnabled"
					@input="onGpcChanged"
				/>

				<!-- User agents -->
				<QuestionCard
					v-if="userAgents && userAgents.length > 0"
					title="Change the Default Device and Browser?"
					:description="userAgentsDescription"
					:show-yes-no-buttons="siteScannerFullAccess"
					:value="provideUserAgent"
					@input="onToggleUserAgent"
				>
					<template v-if="provideUserAgent">
						<div
							class="user-agents-cards"
							data-testid="user-agents-selector-cards"
						>
							<v-row>
								<v-col cols="6">
									<Dropdown
										:value="siteScan.userAgent"
										label="User Agent"
										:items="formatUserAgents"
										:disabled="!siteScannerFullAccess"
										@change="onUserAgentChanged"
									/>
								</v-col>
							</v-row>
						</div>
					</template>
				</QuestionCard>
			</div>
		</transition>
	</QuestionGroup>
</template>
<script>
import { QuestionCard, QuestionGroup } from '../../../../components/component-library-wrapper.js'
import TextField from '../../../../../../shared/components/text-field.vue'
import Dropdown from '../../../../../../shared/components/dropdown.vue'
import OptionSelector from '../../../../../../shared/components/option-selector.vue'
import ValueSlider from '../../../../../../shared/components/value-slider.vue'

export default {
	name: 'scan-advanced-step',
	components: { QuestionCard, QuestionGroup, TextField, Dropdown, OptionSelector, ValueSlider },
	props: {
		siteScan: { type: Object, required: true },
		updateScanRequest: { type: Function, required: true },
		loginTemplateItems: { type: Array, default: () => [] },
		loginTemplateDescription: { type: String, default: '' },
		siteScannerFullAccess: { type: Boolean, default: false },
		showRobotsTxtToggle: { type: Boolean, default: false },
		userAgents: { type: Array, default: () => [] }
	},
	data () {
		return {
			advancedEnabled: false,
			provideBannerInteraction: false,
			provideDomains: false,
			providePerformanceSettings: false,
			provideLoginTemplate: false,
			provideUserAgent: false
		}
	},
	computed: {
		buttonSelectorTooltip () {
			return [
				'Specify the Class or ID of the consent banner button to click.',
				'Classes start with \'.\' e.g. .accept_all_cookies'.replace(/"/g, '\''),
				'IDs start with \'#\' e.g. #reject_all_cookies'.replace(/"/g, '\'')
			].join(' ')
		},
		// simple boolean now
		showAdvancedBlock () { return this.advancedEnabled },
		normalizedPageTimeout () {
			// Coerce once, map legacy ms to seconds, clamp, and always return a number
			const n = Number(this.siteScan.pageTimeout)
			if (!Number.isFinite(n)) return 5
			// Legacy value: 5000ms should render as 5s
			if (n === 5000) return 5
			return Math.max(1, Math.min(10, n))
		},
		userAgentsDescription () {
			const defaultUserAgent = this.userAgents.find(agent => agent.isDefault)
			return `The scanner will run as ${defaultUserAgent.displayText} in its default setting.`
		},
		formatUserAgents () {
			return this.userAgents.map(agent =>
				({ text: agent.displayText, value: agent.id })
			)
		}
	},
	created () {
		// Initialize advancedEnabled if pre-populated advanced options exist
		const s = this.siteScan || {}
		const hasButtonSelector = !!s.buttonSelector
		const hasDomains = Array.isArray(s.allowedDomains) && s.allowedDomains.length > 0
		const pt = s.pageTimeout
		const ptNum = (pt === 0 || pt) ? Number(pt) : null
		const pageTimeoutIsAdvanced = ptNum != null && !isNaN(ptNum) && ptNum !== 5 && ptNum !== 5000
		const hasLoginTemplate = !!s.loginTemplate
		const hasRobots = !!s.followRobotsTxt
		const hasHeadless = !!s.headless
		const hasGpc = !!s.gpcEnabled
		const defaultUserAgent = this.userAgents.find(agent => agent.isDefault)
		const hasDefaultUserAgent = s.userAgent !== defaultUserAgent.id

		const anyAdvanced = hasButtonSelector || hasDomains || pageTimeoutIsAdvanced || hasLoginTemplate || hasRobots || hasHeadless || hasGpc || hasDefaultUserAgent
		if (anyAdvanced) {
			this.advancedEnabled = true
			// Mirror prepopulated fields into toggles so their sections are shown
			this.provideBannerInteraction = hasButtonSelector
			this.provideDomains = hasDomains
			this.providePerformanceSettings = pageTimeoutIsAdvanced
			this.provideLoginTemplate = hasLoginTemplate
			this.provideUserAgent = hasDefaultUserAgent
		}
	},
	methods: {
		onToggleAdvanced (enabled) {
			this.advancedEnabled = enabled === true
			if (!this.advancedEnabled) this.resetAdvanced()
		},
		onToggleBannerInteraction (val) {
			this.provideBannerInteraction = val === true
			if (!this.provideBannerInteraction) this.updateScanRequest('buttonSelector', null)
		},
		onToggleDomains (val) {
			this.provideDomains = val === true
			if (!this.provideDomains) this.updateScanRequest('allowedDomains', [])
		},
		onTogglePerformance (val) {
			this.providePerformanceSettings = val === true
			if (this.providePerformanceSettings) {
				// Set a default if none chosen yet
				if (this.siteScan.pageTimeout == null) this.updateScanRequest('pageTimeout', 5)
			} else {
				this.updateScanRequest('pageTimeout', 5)
			}
		},
		onToggleLoginTemplate (val) {
			this.provideLoginTemplate = val === true
			if (!this.provideLoginTemplate) this.updateScanRequest('loginTemplate', null)
		},
		onDomainsUpdated (val) {
			this.updateScanRequest('allowedDomains', val)
		},
		onPageTimeoutChanged (val) {
			// Clamp and persist
			let next = parseInt(val, 10)
			if (isNaN(next)) next = 5
			if (next < 1) next = 1
			if (next > 10) next = 10
			this.updateScanRequest('pageTimeout', next)
		},
		onRobotsToggle (val) {
			this.updateScanRequest('followRobotsTxt', val)
		},
		onHeadlessChanged (val) {
			this.updateScanRequest('headless', val)
		},
		onLoginTemplateChanged (val) {
			this.updateScanRequest('loginTemplate', val)
		},
		onGpcChanged (val) {
			this.updateScanRequest('gpcEnabled', val)
		},
		resetAdvanced () {
			this.updateScanRequest('buttonSelector', null)
			this.updateScanRequest('allowedDomains', [])
			this.updateScanRequest('followRobotsTxt', false)
			this.updateScanRequest('headless', false)
			this.updateScanRequest('loginTemplate', null)
			this.updateScanRequest('gpcEnabled', false)
			this.updateScanRequest('pageTimeout', 5000)
			const defaultUserAgent = this.userAgents.find(agent => agent.isDefault)
			this.updateScanRequest('userAgent', defaultUserAgent.id)
			this.provideBannerInteraction = false
			this.provideDomains = false
			this.providePerformanceSettings = false
			this.provideLoginTemplate = false
			this.provideUserAgent = false
		},
		onToggleUserAgent (val) {
			this.provideUserAgent = val === true
			if (!this.provideUserAgent) {
				const defaultUserAgent = this.userAgents.find(agent => agent.isDefault)
				this.updateScanRequest('userAgent', defaultUserAgent.id)
			}
		},
		onUserAgentChanged (val) {
			this.updateScanRequest('userAgent', val)
		}
	}
}
</script>
<style scoped>
.advanced-dynamic-group { display: flex; flex-direction: column; gap: 20px; margin-top: 8px; }
.fade-expand-enter-active,
.fade-expand-leave-active { transition: opacity .24s ease, transform .24s ease; }
.fade-expand-enter,
.fade-expand-leave-to { opacity: 0; transform: translateY(-6px); }
.fade-expand-enter-to,
.fade-expand-leave { opacity: 1; transform: translateY(0); }
.card-actions { display: flex; gap: 8px; margin-top: 8px; }
</style>
